import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    // AuthCallback will exchange the session_id and establish the session.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // "Remember me": when true → persistent localStorage; when false →
  // sessionStorage (cleared when the browser closes).
  const _saveToken = (token, remember = true) => {
    if (!token) return;
    try {
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("auth_token");
    } catch {}
    try {
      (remember ? localStorage : sessionStorage).setItem("auth_token", token);
    } catch {}
  };

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) _saveToken(data.token, remember);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) _saveToken(data.token, true);
    setUser(data);
    return data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    if (data.token) _saveToken(data.token, true);
    setUser(data);
    return data;
  };

  const otpSend = async (phone_e164) => {
    const { data } = await api.post("/auth/otp/send", { phone_e164 });
    return data;
  };

  const otpVerify = async (phone_e164, code, name = "", remember = true) => {
    const { data } = await api.post("/auth/otp/verify", { phone_e164, code, name });
    if (data.token) _saveToken(data.token, remember);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { localStorage.removeItem("auth_token"); sessionStorage.removeItem("auth_token"); } catch {}
    setUser(null);
  };

  // Memoise so consumers only re-render when one of the actual values
  // changes — not on every parent render. The function identities are
  // stable references (defined at module scope of this provider).
  const value = useMemo(() => ({
    user, loading, login, register, googleLogin, exchangeGoogleSession,
    otpSend, otpVerify, logout, refresh: checkAuth,
  }), [user, loading, checkAuth]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
